import {BaseBadge, BaseButton, BaseCard} from "@/components";
import {usePromotionConditions, useUpdatePromotionCondition} from "@/hooks/useLevels";
import type {PromotionCondition, UpdatePromotionConditionBody} from "@/types/levels.types";
import {Pencil} from "lucide-react";
import {useState} from "react";
import {LEVEL_NAMES, formatCents} from "../levels.utils";

export function PromotionConditionsTab() {
  const {data: conditions, isLoading} = usePromotionConditions();
  const [editing, setEditing] = useState<PromotionCondition | null>(null);

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {["sk-cond-0", "sk-cond-1"].map(id => (
          <BaseCard
            key={id}
            padding="md"
          >
            <div className="space-y-3">
              {["a", "b", "c", "d", "e"].map(k => (
                <div
                  key={`${id}-${k}`}
                  className="h-4 bg-(--color-surface-2) rounded animate-pulse"
                />
              ))}
            </div>
          </BaseCard>
        ))}
      </div>
    );
  }

  const items = conditions ?? [];

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2">
        {items.map(cond => (
          <ConditionCard
            key={cond.id}
            condition={cond}
            onEdit={() => setEditing(cond)}
          />
        ))}
        {items.length === 0 && (
          <p className="col-span-2 text-center text-(--color-text-tertiary) py-12">
            No hay condiciones de promoción configuradas.
          </p>
        )}
      </div>
      {editing && (
        <EditConditionModal
          condition={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

/* ── Condition Card ────────────────────────────────────────── */

function ConditionCard({condition, onEdit}: {condition: PromotionCondition; onEdit: () => void}) {
  const from = LEVEL_NAMES[condition.fromLevel] ?? `Level ${condition.fromLevel}`;
  const to = LEVEL_NAMES[condition.toLevel] ?? `Level ${condition.toLevel}`;

  return (
    <BaseCard padding="md">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BaseBadge
            variant="neutral"
            label={from}
          />
          <span className="text-(--color-text-tertiary)">→</span>
          <BaseBadge
            variant="info"
            label={to}
          />
          <BaseBadge
            variant={condition.isActive ? "success" : "danger"}
            label={condition.isActive ? "Activo" : "Inactivo"}
            dot
          />
        </div>
        <BaseButton
          variant="ghost"
          size="sm"
          onClick={onEdit}
          title="Editar"
        >
          <Pencil size={14} />
        </BaseButton>
      </div>

      <div className="space-y-2 text-sm">
        <Row
          label="Envíos exitosos mín."
          value={String(condition.minSuccessfulSends)}
        />
        <Row
          label="Días registrado mín."
          value={String(condition.minDaysRegistered)}
        />
        <Row
          label="Acumulado enviado mín."
          value={formatCents(condition.minAccumulatedSentCents)}
        />
        <Row
          label="Nivel KYC requerido"
          value={String(condition.kycLevelRequired)}
        />
        <Row
          label="Días sin alertas"
          value={String(condition.noAlertsDays)}
        />
        <BoolRow
          label="Teléfono verificado"
          value={condition.phoneVerified}
        />
        <BoolRow
          label="2FA requerido"
          value={condition.twoFactorRequired}
        />
      </div>
    </BaseCard>
  );
}

function Row({label, value}: {label: string; value: string}) {
  return (
    <div className="flex justify-between">
      <span className="text-(--color-text-secondary)">{label}</span>
      <span className="font-medium text-(--color-text-primary)">{value}</span>
    </div>
  );
}

function BoolRow({label, value}: {label: string; value: boolean}) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-(--color-text-secondary)">{label}</span>
      <BaseBadge
        variant={value ? "success" : "neutral"}
        label={value ? "Sí" : "No"}
      />
    </div>
  );
}

/* ── Edit Condition Modal ──────────────────────────────────── */

function EditConditionModal({condition, onClose}: {condition: PromotionCondition; onClose: () => void}) {
  const updateMutation = useUpdatePromotionCondition();
  const [form, setForm] = useState({
    minSuccessfulSends: String(condition.minSuccessfulSends),
    minDaysRegistered: String(condition.minDaysRegistered),
    minAccumulatedSentCents: String(Number(condition.minAccumulatedSentCents) / 100),
    kycLevelRequired: String(condition.kycLevelRequired),
    noAlertsDays: String(condition.noAlertsDays),
    phoneVerified: condition.phoneVerified,
    twoFactorRequired: condition.twoFactorRequired,
    isActive: condition.isActive,
  });
  const [error, setError] = useState("");

  const set = (key: string, value: string | boolean) => setForm(prev => ({...prev, [key]: value}));

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");

    const body: UpdatePromotionConditionBody = {
      minSuccessfulSends: Number(form.minSuccessfulSends),
      minDaysRegistered: Number(form.minDaysRegistered),
      minAccumulatedSentCents: String(Math.round(Number(form.minAccumulatedSentCents) * 100)),
      kycLevelRequired: Number(form.kycLevelRequired),
      noAlertsDays: Number(form.noAlertsDays),
      phoneVerified: form.phoneVerified,
      twoFactorRequired: form.twoFactorRequired,
      isActive: form.isActive,
    };

    try {
      await updateMutation.mutateAsync({conditionId: condition.id, body});
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al actualizar la condición");
    }
  };

  const from = LEVEL_NAMES[condition.fromLevel] ?? `Level ${condition.fromLevel}`;
  const to = LEVEL_NAMES[condition.toLevel] ?? `Level ${condition.toLevel}`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-(--color-text-primary) mb-4">
          Editar Condición — {from} → {to}
        </h2>
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
        >
          <p className="text-xs text-(--color-text-tertiary) -mt-2">El monto acumulado se ingresa en dólares.</p>

          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Envíos exitosos mín."
              value={form.minSuccessfulSends}
              onChange={v => set("minSuccessfulSends", v)}
              type="number"
            />
            <Field
              label="Días registrado mín."
              value={form.minDaysRegistered}
              onChange={v => set("minDaysRegistered", v)}
              type="number"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Acumulado enviado ($)"
              value={form.minAccumulatedSentCents}
              onChange={v => set("minAccumulatedSentCents", v)}
              type="number"
              step="0.01"
            />
            <Field
              label="Días sin alertas"
              value={form.noAlertsDays}
              onChange={v => set("noAlertsDays", v)}
              type="number"
            />
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-(--color-text-secondary)">Nivel KYC requerido</span>
            <select
              value={form.kycLevelRequired}
              onChange={e => set("kycLevelRequired", e.target.value)}
              className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
            >
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
            </select>
          </label>

          <div className="flex flex-wrap gap-4 py-2">
            <Toggle
              label="Teléfono verificado"
              checked={form.phoneVerified}
              onChange={v => set("phoneVerified", v)}
            />
            <Toggle
              label="2FA requerido"
              checked={form.twoFactorRequired}
              onChange={v => set("twoFactorRequired", v)}
            />
            <Toggle
              label="Activo"
              checked={form.isActive}
              onChange={v => set("isActive", v)}
            />
          </div>

          {error && <p className="text-sm text-(--color-danger)">{error}</p>}

          <div className="flex justify-end gap-2 mt-2">
            <BaseButton
              variant="ghost"
              size="md"
              onClick={onClose}
            >
              Cancelar
            </BaseButton>
            <BaseButton
              variant="primary"
              size="md"
              loading={updateMutation.isPending}
            >
              Guardar
            </BaseButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  step,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  step?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-(--color-text-secondary)">{label}</span>
      <input
        type={type}
        step={step}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="px-3 py-2 rounded-md border border-(--color-border) text-sm"
      />
    </label>
  );
}

function Toggle({label, checked, onChange}: {label: string; checked: boolean; onChange: (v: boolean) => void}) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-(--color-border) accent-(--color-primary)"
      />
      <span className="text-sm text-(--color-text-secondary)">{label}</span>
    </label>
  );
}
